import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

/**
 * MermaidDiagram Component
 * Renders a Mermaid diagram from code definition
 * 
 * Best practices:
 * - Uses refs to ensure stable DOM element
 * - Handles async rendering properly
 * - Prevents React re-renders from clearing SVG
 * - Provides error handling and fallback
 */
const MermaidDiagram = ({ code, id }) => {
  const diagramRef = useRef(null);
  const [error, setError] = useState(null);
  const [isRendering, setIsRendering] = useState(false);

  useEffect(() => {
    // Only render if we have code and a ref
    if (!code || !diagramRef.current) return;

    // Skip if already rendered
    if (diagramRef.current.querySelector('svg')) return;

    // Clear any previous error
    setError(null);
    setIsRendering(true);

    // Generate unique ID for this diagram
    const diagramId = id || `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Store ref to check if component is still mounted
    const currentRef = diagramRef.current;
    
    // Set the element ID
    currentRef.id = diagramId;

    // Initialize Mermaid (always initialize to ensure it's ready)
    const initMermaid = async () => {
      try {
        await mermaid.initialize({
          startOnLoad: false,
          theme: 'default',
          securityLevel: 'loose',
          flowchart: {
            useMaxWidth: true,
            htmlLabels: true
          }
        });
      } catch (initError) {
        console.warn('Mermaid initialization warning:', initError);
        // Continue anyway - might already be initialized
      }
    };

    // Render the diagram
    const renderDiagram = async () => {
      try {
        // Ensure Mermaid is initialized
        await initMermaid();

        // Use Mermaid's render method (works with v9, v10, and v11)
        const result = await mermaid.render(diagramId, code);
        
        // Handle different result formats
        let svgContent;
        if (typeof result === 'string') {
          svgContent = result;
        } else if (result && result.svg) {
          svgContent = result.svg;
        } else if (result && typeof result === 'object' && 'svg' in result) {
          svgContent = result.svg;
        } else {
          throw new Error('Mermaid render returned invalid result');
        }

        // Only update if ref still exists and is still in the DOM (component not unmounted)
        if (diagramRef.current && diagramRef.current === currentRef && diagramRef.current.parentNode) {
          try {
            // Clear existing content safely
            while (diagramRef.current.firstChild) {
              diagramRef.current.removeChild(diagramRef.current.firstChild);
            }
            // Create a temporary container to parse the SVG
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = svgContent;
            // Move nodes instead of using innerHTML to avoid React conflicts
            while (tempDiv.firstChild) {
              diagramRef.current.appendChild(tempDiv.firstChild);
            }
            
            // Call bindFunctions if available (for interactive diagrams)
            if (result.bindFunctions && typeof result.bindFunctions === 'function') {
              result.bindFunctions(diagramRef.current);
            }
            
            setIsRendering(false);
          } catch (domError) {
            console.warn('DOM update error in MermaidDiagram:', domError);
            setIsRendering(false);
          }
        }
      } catch (err) {
        console.error('Mermaid render error:', err);
        setError(err.message || 'Failed to render diagram');
        setIsRendering(false);
        
        // Show code as fallback - use safe DOM manipulation
        if (diagramRef.current && diagramRef.current === currentRef && diagramRef.current.parentNode) {
          try {
            // Clear existing content safely
            while (diagramRef.current.firstChild) {
              diagramRef.current.removeChild(diagramRef.current.firstChild);
            }
            const pre = document.createElement('pre');
            pre.style.cssText = 'padding: 1rem; background: #f5f5f5; border: 1px solid #ddd; border-radius: 4px; overflow-x: auto; font-size: 12px;';
            pre.textContent = code;
            diagramRef.current.appendChild(pre);
          } catch (domError) {
            console.warn('DOM fallback error in MermaidDiagram:', domError);
          }
        }
      }
    };

    renderDiagram();

    // Cleanup function
    return () => {
      // Don't clear on unmount - let React handle it
      setIsRendering(false);
    };
  }, [code, id]);

  return (
    <div 
      ref={diagramRef}
      className="mermaid-diagram"
      style={{
        margin: '1rem 0',
        textAlign: 'center',
        minHeight: isRendering ? '100px' : 'auto'
      }}
    >
      {isRendering && (
        <div style={{ padding: '1rem', color: '#666' }}>
          Rendering diagram...
        </div>
      )}
      {error && (
        <div style={{ padding: '1rem', color: '#d32f2f', background: '#ffebee', borderRadius: '4px' }}>
          Error: {error}
        </div>
      )}
    </div>
  );
};

export default MermaidDiagram;

